import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewChildren,
  HostListener,
  AfterViewInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AuditService,
  ConfigService,
  IngredientChoice,
  IngredientChoicesType,
  OrderableIngredient,
  OrderableItem,
  OrderedChoice,
  OrderedChoiceService,
  OrderedIngredient,
  OrderedItem,
  OrderedItemService,
  SizePrice,
  StoreConfig,
  Upsell,
  Utils,
  IngredientChoiceGroup,
  UpsellType,
  OrderableItemService,
} from 'ngx-web-api';
import { ItemEditorService } from 'app/core/services/item-editor.service';
import { distinctUntilChanged, map, filter, takeUntil } from 'rxjs/operators';
import { Subscription, Subject, noop, combineLatest } from 'rxjs';
import { EditingItemChoicesService } from '../../../core/services/editing-item-choices.service';
import { SpecialWizardEditingItemChoicesService } from '../../../core/services/special-wizard-editing-item-choices.service';
import { IngredientSelection } from '../../../domain/ingredient-selection';
import { Theme } from '../../../domain/theme.model';
import { StageIngredientAction } from '../../../item-editor/domain/stage-ingredient-action.model';
import { ExtraIngredientUpsellService } from 'app/core/services/extra-ingredient-upsell.service';
import { ExtraUpsell } from 'app/domain/extra-upsell';
import { ViewportService } from 'app/core/services/viewport.service';
import { ViewPort } from 'app/domain/view-port.model';
import { faAngleRight, faAngleLeft } from '@fortawesome/pro-light-svg-icons';
import { CanMoveToStepChoicePipe } from 'app/shared/pipes/can-move-to-step-choice.pipe';
import { FilterOutInstructionPipe } from 'app/shared/pipes/filter-out-instruction.pipe';
import { HighlightElementsService } from 'app/shared/highlight-elements.service';
import { BreakpointService } from 'app/core/services/breakpoint.service';
import { HalfsOrderingService } from 'app/core/services/halfs-ordering.service';

@Component({
  selector: 'fts-item-choices',
  templateUrl: './item-choices.component.html',
  styleUrls: ['./item-choices.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemChoicesComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input()
  isInModal: boolean;
  @Input()
  orderableItem: OrderableItem;
  @Input()
  item: OrderedItem;
  @Input()
  upsellItems: Upsell[];
  @Input()
  wizardStepIndex = 0;
  @Input()
  price: number;
  @Input()
  hasGraphicalBuilder: boolean;
  @Input()
  theme: Theme;
  @Input()
  updatingOrderSubscription: Subscription;
  @Input()
  inItemEditorModal = false;

  @Output()
  choiceClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()
  ingredientChange: EventEmitter<IngredientSelection[]> = new EventEmitter<IngredientSelection[]>();
  @Output()
  instructionsChange: EventEmitter<string> = new EventEmitter();
  @Output()
  stageAction: EventEmitter<StageIngredientAction> = new EventEmitter();
  @Output()
  orderUpdate: EventEmitter<any> = new EventEmitter();
  @Output()
  handledSizeChange: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  itemChange: EventEmitter<OrderedItem> = new EventEmitter<OrderedItem>();
  @Output()
  cancelBtnClick = new EventEmitter<void>();
  @Output()
  calculateIngredients = new EventEmitter<void>();

  hideUpsellItems = false;

  @ViewChildren('scrollLink')
  private scrollLinks;

  @ViewChild('scrollLinksWrapper', { static: false })
  private scrollLinksWrapper: ElementRef;

  @ViewChild('stepsWrapper', { static: false })
  private stepsWrapper: ElementRef;

  filteredChoices: IngredientChoice[];

  ingredientChoicesTypes = IngredientChoicesType;
  choices: IngredientChoice[] = [];
  visibleChoice?: IngredientChoice;
  nextChoice?: IngredientChoice;
  nextChoiceGroup?: IngredientChoiceGroup;
  previousChoiceGroup?: IngredientChoiceGroup;
  ingredientChoicesType: IngredientChoicesType;
  isLastChoiceVisible = false;
  isFirstChoiceVisible = false;
  visibleOrderedChoice: OrderedChoice;
  caloriesDisclaimer?: string;
  hasItemCalorieInfo: boolean;
  choicesInit = true;
  choicesService: EditingItemChoicesService;
  leftStepsWrapperPosition: string;
  hasIngredientChoices: boolean;
  defaultIngredientsPerSizeNotChecked: Array<OrderableIngredient> = [];
  extraUpsell: ExtraUpsell;
  deniedExtra = false;
  isMobile = false;
  orderedItemInitialized = false;
  changeRecipeTo: string;
  addBtnLabel: string;
  isItemValid: boolean;
  shouldShowBackBtn: boolean;
  shouldShowForwardBtn: boolean;
  shouldShowSubmitBtn: boolean;
  canMoveToStepChoice = new CanMoveToStepChoicePipe();
  filterOutInstruction = new FilterOutInstructionPipe();

  faAngleRight = faAngleRight;
  faAngleLeft = faAngleLeft;

  private subscriptions: Subscription[] = [];
  private destroy$: Subject<void> = new Subject<void>();

  isHalfAndHalf = false;

  constructor(
    private auditService: AuditService,
    private itemEditorService: ItemEditorService,
    public orderedItemService: OrderedItemService,
    private editingItemChoices: EditingItemChoicesService,
    private configService: ConfigService,
    private orderedChoiceService: OrderedChoiceService,
    private specialWizardEditingItemChoices: SpecialWizardEditingItemChoicesService,
    private extraIngredientService: ExtraIngredientUpsellService,
    private viewportService: ViewportService,
    private orderableItemService: OrderableItemService,
    private highlightService: HighlightElementsService,
    public breakPointService: BreakpointService,
    private halfsOrderingService: HalfsOrderingService
  ) {}

  @HostListener('window:resize')
  onResize() {
    this.calculateLeftStepsWrapperPosition();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event) {
    if (this.extraIngredientService.isPopupOpen()) {
      this.extraIngredientService.setClickEvent(event);
    }
  }

  ngOnInit() {
    if (!!this.orderableItem.caloriesDisclaimer) {
      this.caloriesDisclaimer = this.orderableItem.caloriesDisclaimer;
    } else {
      const storeConfigSub = this.configService.storeConfig$
        .pipe(map((c: StoreConfig) => c.caloriesDisclaimer))
        .subscribe((caloriesDisclaimer: string) => (this.caloriesDisclaimer = caloriesDisclaimer));

      this.subscriptions.push(storeConfigSub);
    }

    this.ingredientChoicesType = this.itemEditorService.getChoicesLayout(this.orderableItem);
    this.subscriptions.push(
      this.subscribeToSizeChanges(),
      this.subscribeToVisibleChoiceChanges(),
      this.subscribeToVisibleOrderedChoiceChanges(),
      this.extraIngredientService.extraUpsell$.subscribe(extraUpsell => (this.extraUpsell = extraUpsell)),
      this.extraIngredientService.deniedExtraUpsell$.subscribe(deniedExtraUpsell => (this.deniedExtra = deniedExtraUpsell))
    );

    this.item.choices.forEach(choice => {
      const orderableChoice = this.orderableItem.getChoice(choice.name);
      if (orderableChoice && !!choice.ingredients) {
        orderableChoice.ingredients
          .filter(
            orderableIngredient =>
              orderableIngredient.isDefault &&
              orderableIngredient.getSizePrice(this.item.size) &&
              !choice.ingredients.find(ing => ing.ingredient === orderableIngredient.name)
          )
          .forEach(orderableIngredient => {
            this.defaultIngredientsPerSizeNotChecked.push(orderableIngredient);
          });
      }
    });

    this.viewportService.viewport$
      .pipe(
        map((viewport: ViewPort) => viewport.isMobile),
        distinctUntilChanged()
      )
      .subscribe((isMobile: boolean) => (this.isMobile = isMobile));

    this.halfsOrderingService.isHalfAndHalf$.subscribe(v => (this.isHalfAndHalf = v));

    combineLatest([this.halfsOrderingService.leftHalfOrderedItem$, this.halfsOrderingService.rightHalfOrderedItem$]).subscribe(
      ([leftOrderedItem, rightOrderedItem]) => {
        if (!this.isHalfAndHalf) {
          return;
        }
        this.halfsOrderingService.calculateItemWithHalfs(this.item, this.orderableItem, leftOrderedItem, rightOrderedItem);
        this.validateOrderedItem();
        this.ingredientChange.emit();
        this.itemEditorService.itemIngredientsChanged();
        this.calculateIngredients.emit();
      }
    );
  }

  ngAfterViewInit() {
    this.calculateLeftStepsWrapperPosition();
  }

  ngOnChanges(changes: any): void {
    if (changes['isInModal'] || !this.choicesService) {
      this.choicesService = this.isInModal ? this.specialWizardEditingItemChoices : this.editingItemChoices;
    }

    const hasItemBeenInitialized = changes['orderableItem'] && changes['orderableItem'].firstChange;
    const hasItemChanged =
      changes['orderableItem'] &&
      !changes['orderableItem'].firstChange &&
      (changes.orderableItem.previousValue.category !== changes.orderableItem.currentValue.category ||
        changes.orderableItem.previousValue.name !== changes.orderableItem.currentValue.name);

    if (changes['orderableItem'] || changes['wizardStepIndex']) {
      const resetToFirstChoice = changes['wizardStepIndex'] || hasItemChanged || hasItemBeenInitialized;
      this.ingredientChoicesType = this.itemEditorService.getChoicesLayout(this.orderableItem);
      this.hasItemCalorieInfo = this.determineIfItemHasCalorieInfo();
      this.filteredChoices = this.orderedItemService.getFilteredIngredientChoices(changes.orderableItem.currentValue, this.item);
      this.identifyVisibleChoices();
      this.calculateNextAndPreviousChoiceGroup();
      this.initVisibleChoice(changes.orderableItem.currentValue, resetToFirstChoice);
      if (resetToFirstChoice) {
        this.initVisibleChoiceGroup(changes.orderableItem.currentValue);
      }
    }

    if (changes['theme']) {
      this.ingredientChoicesType = this.itemEditorService.getChoicesLayout(this.orderableItem);
    }

    if (changes['item']) {
      if (!!this.item && !this.orderedItemInitialized) {
        this.findOrderedDeterminingRecipeIngredient();
      }
      this.identifyVisibleChoices();
      this.calculateNextAndPreviousChoiceGroup();
      this.validateOrderedItem();
      this.calcAddBtnLabel();
    }

    if (changes['orderableItem'] || changes['item']) {
      this.hasIngredientChoices = !!this.orderedItemService.getFilteredIngredientChoices(this.orderableItem, this.item).length;
      this.calcShouldShowBackBtn();
    }
  }

  ngOnDestroy(): void {
    this.choicesService.setVisibleChoice(); // clean up service state
    this.choicesService.setVisibleChoiceGroup();
    this.choicesService.setVisibleOrderedChoice();
    this.subscriptions.forEach(s => s.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLoadItemEditorModalHeaderComponent({ scrollLinksWrapper, scrollLinks }) {
    this.scrollLinksWrapper = scrollLinksWrapper;
    this.scrollLinks = scrollLinks;
  }

  calcAddBtnLabel() {
    this.addBtnLabel = this.editingItemChoices.getSubmitBtnLabel(this.item, !!this.theme);
  }

  calcShouldShowBackBtn() {
    this.shouldShowBackBtn =
      this.ingredientChoicesType === IngredientChoicesType.Steps &&
      !this.isFirstChoiceVisible &&
      this.orderedItemService.getFilteredIngredientChoices(this.orderableItem, this.item).length > 0;
    this.shouldShowForwardBtn =
      this.ingredientChoicesType === IngredientChoicesType.Steps &&
      !this.isLastChoiceVisible &&
      this.orderedItemService.getFilteredIngredientChoices(this.orderableItem, this.item).length > 0;
    this.shouldShowSubmitBtn = this.editingItemChoices.shouldShowSubmitBtn(
      this.orderableItem,
      this.item,
      this.ingredientChoicesType,
      this.isLastChoiceVisible
    );
  }

  onOrderUpdate() {
    if (!this.isItemValid) {
      this.editingItemChoices.handleInvalidSubmit(this.orderableItem, this.item, this.ingredientChoicesType);
    }
    this.orderUpdate.emit();
  }

  upsellItemAdded(result: string) {
    this.hideUpsellItems = !!result;
  }

  trackByChoiceFn(_index, choice: IngredientChoice) {
    return choice.name;
  }

  toggleIngredients(selections: IngredientSelection[]) {
    this.changeRecipeTo = undefined;
    // Find toggled ingredients that determine recipe
    // Note that these can be
    // 0 (none),
    // 1 (how many = 1)
    // 2 (how many = 1, enforced = 1) => when choice ingredients are of type radio selection
    // we remove 1 and add 1 ingredient at the same time
    const determiningRecipeSelections = selections.filter(s => s.ingredient.determinesRecipe);

    if (determiningRecipeSelections.length) {
      // If the recipe determining ingredient is one (1) and was already toggled, this is a removal and we should switch to base recipe
      if (
        determiningRecipeSelections.length === 1 &&
        determiningRecipeSelections.find(drs => drs.ingredient.name === this.changeRecipeTo)
      ) {
        this.changeRecipeTo = 'base';
        // Otherwise this is either :
        // a. an addition of a recipe determining ingredient (length: 1 and not already selected)
        // b. radio selection change of recipe determinig ingredient (length: 2)
        // c. an addition of a NON-recipe determining ingredient (length: 0)
      } else {
        this.changeRecipeTo = determiningRecipeSelections.find(drs => drs.ingredient.name !== this.changeRecipeTo).ingredient.name;
      }
    }
    // Trigger recipe change stream
    // For cases a. and b. above the recipe will change and the stream will be triggered
    // For case c. the same recipe will be set so no re-fetch stream will be triggered
    if (!!this.changeRecipeTo) {
      this.itemEditorService.setRecipe(this.changeRecipeTo);
    }
    selections.forEach(selection => this.toggleIngredient(selection));
    this.validateOrderedItem();
    this.ingredientChange.emit(selections);
    this.itemEditorService.itemIngredientsChanged();
  }

  selectChoice(choice: IngredientChoice, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (
      (this.orderableItem.choiceGroups && this.orderableItem.choiceGroups.length) ||
      this.ingredientChoicesType !== this.ingredientChoicesTypes.Steps ||
      this.canMoveToStepChoice.transform(choice, this.choices, this.visibleChoice, this.item)
    ) {
      this.choicesService.setVisibleChoice(choice);
    }
  }

  selectedChoice(choice: IngredientChoice) {
    this.choicesService.setVisibleChoice(choice);
  }

  chooseHalf(selection: IngredientSelection) {
    if (this.item && this.item.hasIngredient(selection.choice, selection.ingredient)) {
      const orderedIngredient = this.getOrderedIngredient(selection.choice, selection.ingredient);
      if (orderedIngredient) {
        orderedIngredient.chooseHalf(selection.isLeftHalf, selection.isRightHalf);
        this.auditService.createAudit(() => {
          let half;
          if (selection.isSecondHalf) {
            half =
              selection.isSecondHalf && selection.isRightHalf
                ? 'left-half'
                : selection.isSecondHalf && selection.isLeftHalf
                ? 'right-half'
                : 'whole';
          } else {
            half = selection.isLeftHalf ? 'left-half' : selection.isRightHalf ? 'right-half' : 'whole';
          }
          return `Selected ${selection.isSecondHalf ? 'second' : ''} "${half}" for "${selection.ingredient.name}" ingredient from "${
            selection.choice.name
          }" choice`;
        });
      }
    } else {
      this.addIngredient(selection.choice, selection.ingredient, selection.isLeftHalf, selection.isRightHalf);
    }
    this.validateOrderedItem();
    /**
     * Prevent the emitter to be called on half and half editor with halve ingredients
     * since it is called when the left or right half ordered item are mutated
     * @see {ngOnInit#combineLatest}
     */
    if (!(this.isHalfAndHalf && selection.ingredient.allowHalves)) {
      this.ingredientChange.emit();
      this.itemEditorService.itemIngredientsChanged();
    }
  }

  toggleQualifier(selection: IngredientSelection) {
    if (this.item && this.item.hasIngredient(selection.choice, selection.ingredient)) {
      const orderedIngredient = this.getOrderedIngredient(selection.choice, selection.ingredient);

      if (orderedIngredient) {
        const qualName = selection.qualifier.name;
        const selectedChoice = selection.choice.name;
        const ingredientName = orderedIngredient.ingredient;
        const itemName = this.item.item;
        const qualsBefore = (orderedIngredient.qualifiers || []).length;
        const qualProp = selection.isSecondHalf ? 'secondHalfQualifiers' : 'qualifiers';
        if (this.isHalfAndHalf && selection.ingredient.allowHalves) {
          this.halfsOrderingService.updateIngredientQualifiers(selection);
        } else {
          if (!orderedIngredient[qualProp]) {
            orderedIngredient[qualProp] = [qualName];
            this.auditService.createAudit(
              () =>
                `Ordered '${qualName}' ${selection.isSecondHalf ? 'second' : ''} qualifier for '${ingredientName}' ` +
                `ingredient from '${selectedChoice}' on '${itemName}' item`
            );
          } else {
            const qualifierIndex = orderedIngredient[qualProp].findIndex(qualifier => qualifier === qualName);
            if (qualifierIndex >= 0) {
              orderedIngredient[qualProp].splice(qualifierIndex, 1);
              this.auditService.createAudit(
                () =>
                  `Removed '${qualName}' ${selection.isSecondHalf ? 'second' : ''} qualifier from '${ingredientName}' ` +
                  `ingredient from '${selectedChoice}' on '${itemName}' item`
              );
            } else {
              const sameGroupQualifierIndex = orderedIngredient[qualProp]
                .map(qualifier => selection.ingredient.qualifiers.find(q => q.name === qualifier))
                .findIndex(q => !!q && q.type === selection.qualifier.type);

              if (sameGroupQualifierIndex >= 0) {
                const removedQualifier = orderedIngredient[qualProp].splice(sameGroupQualifierIndex, 1);
                this.auditService.createAudit(
                  () =>
                    `Removed '${removedQualifier[0]}' ${selection.isSecondHalf ? 'second' : ''} qualifier from ` +
                    `'${ingredientName}' ingredient from '${selectedChoice}' on '${itemName}' item`
                );
              }
              orderedIngredient[qualProp].push(qualName);
              this.auditService.createAudit(
                () =>
                  `Ordered '${qualName}' ${selection.isSecondHalf ? 'second' : ''} qualifier for '${ingredientName}' ` +
                  `ingredient from '${selectedChoice}' on '${itemName}' item`
              );
            }
          }
        }

        const upsellOnTopOfDefault =
          selection.ingredient.qualifiers.filter(q => q.isDefault).length === (orderedIngredient[qualProp] || []).length;
        if (selection.isExtraUpsell && ((orderedIngredient[qualProp] || []).length > qualsBefore || upsellOnTopOfDefault)) {
          this.item.isUpsell = true;
          this.item.upsellType = UpsellType.EXTRA_INGREDIENT;
          this.extraIngredientService.addExtraUpsellToOrdered(this.item, this.extraUpsell);
        } else if (
          this.item.isUpsell &&
          this.item.upsellType === UpsellType.EXTRA_INGREDIENT &&
          (orderedIngredient[qualProp] || []).length <= qualsBefore &&
          this.extraUpsell &&
          this.item.extraIngredientUpsells &&
          this.item.extraIngredientUpsells.map(u => u.ingredient).indexOf(selection.ingredient.name) !== -1
        ) {
          this.extraIngredientService.removeExtraUpsellFromOrdered(this.item, selection.ingredient.name);
          if (this.item.extraIngredientUpsells.length === 0) {
            this.item.isUpsell = false;
            this.item.upsellType = undefined;
          }
        }

        orderedIngredient.isSecondHalf = !!orderedIngredient.secondHalfQualifiers?.length;
        // Create new reference to trigger qualifier choice change detection
        orderedIngredient[qualProp] = Array.from(orderedIngredient[qualProp]);

        const orderedChoice = this.item.choices.find(c => c.name === selection.choice.name);
        this.choicesService.setVisibleOrderedChoice(orderedChoice);

        /**
         * Prevent the emitter to be called on half and half editor with halve ingredients
         * since it is called when the left or right half ordered item are mutated
         * @see {ngOnInit#combineLatest}
         */
        if (!(this.isHalfAndHalf && selection.ingredient.allowHalves)) {
          this.ingredientChange.emit();
        }
      }
    }
  }

  moveToNextChoice() {
    if (this.isLastChoiceVisible || !this.visibleOrderedChoice.isValid()) {
      this.highlightService.highLightAll(true);
      return;
    }
    if (this.nextChoice) {
      this.auditService.createAudit(() => `Moving to next choice : "${this.nextChoice.name}" of "${this.orderableItem.name}"`);
      this.choicesService.setVisibleChoice(this.nextChoice);
    }
  }

  moveToPreviousChoice() {
    if (this.visibleChoice && !this.isFirstChoiceVisible) {
      const previousIndex = this.choiceIndex(this.visibleChoice) - 1;
      const previousChoice = this.choices[previousIndex];
      this.auditService.createAudit(() => `Moving to previous choice : "${previousChoice.name}" of "${this.orderableItem.name}"`);
      this.choicesService.setVisibleChoice(previousChoice);
    }
  }

  getFirstInvalidChoice(): IngredientChoice | undefined {
    const firstInvalidChoice = this.item.choices.find(ch => !ch.isValid());
    if (firstInvalidChoice && this.choices.some(ch => ch.name === firstInvalidChoice.name)) {
      const choice = this.orderableItem.getChoice(firstInvalidChoice.name);
      return !choice || choice.isInstructions ? undefined : choice;
    }
  }

  instructionsChanged(instructions: string) {
    this.instructionsChange.emit(instructions);
  }

  onStageAction(stageAction: StageIngredientAction) {
    this.stageAction.emit(stageAction);
  }

  onChoiceGroupChange(choiceGroup: IngredientChoiceGroup) {
    if (Utils.isNullOrUndefined(choiceGroup)) {
      this.choicesService.setVisibleChoiceGroup(choiceGroup);
    }

    if (choiceGroup && choiceGroup.choices && choiceGroup.choices.length) {
      const filteredGroupChoices = choiceGroup.choices.filter(name => this.choices.some(choice => choice.name === name));
      const firstChoice = this.choices.find(ch => ch.name === filteredGroupChoices[0]);

      if (!Utils.isNullOrUndefined(choiceGroup) && !!firstChoice) {
        this.choicesService.setVisibleChoiceGroup(choiceGroup);
        this.choicesService.setVisibleChoice(firstChoice);
      }
    }
  }

  onCancelBtnClick() {
    this.cancelBtnClick.emit();
  }

  private choiceIndex(choice: IngredientChoice) {
    return choice && choice.name ? this.choices.map(ingredientChoice => ingredientChoice.name).indexOf(choice.name) : -1;
  }

  private toggleIngredient(selection: IngredientSelection) {
    let action;
    if (this.item && this.item.hasIngredient(selection.choice, selection.ingredient)) {
      this.auditService.createAudit(() => {
        return `Removed "${selection.ingredient.name}" ingredient from "${selection.choice.name}" choice on "${this.item.item}" item`;
      });
      action = this.removeIngredient(selection.choice, selection.ingredient);
      if (
        this.item.isUpsell &&
        this.item.upsellType === UpsellType.EXTRA_INGREDIENT &&
        this.item.extraIngredientUpsells.map(u => u.ingredient).indexOf(selection.ingredient.name) !== -1
      ) {
        this.extraIngredientService.removeExtraUpsellFromOrdered(this.item, selection.ingredient.name);
        if (this.item.extraIngredientUpsells.length === 0) {
          this.item.isUpsell = false;
          this.item.upsellType = undefined;
        }
      }
    } else {
      this.auditService.createAudit(() => {
        return `Ordered "${selection.ingredient.name}" ingredient from "${selection.choice.name}" choice on "${this.item.item}" item`;
      });
      action = this.addIngredient(selection.choice, selection.ingredient);

      if (!this.deniedExtra && !this.extraIngredientService.isPopupOpen()) {
        this.extraIngredientService
          .getQualifierUpsell(selection.ingredient, selection.choice, this.item)
          .pipe(
            filter(extraUpsell => !!extraUpsell),
            takeUntil(this.destroy$)
          )
          .subscribe(extraUpsell => this.extraIngredientService.setQualifierUpsell(extraUpsell), noop);
      }
    }

    const updatedVisibleOrderedChoice = this.item.choices.find(c => c.name === this.visibleOrderedChoice.name);
    if (updatedVisibleOrderedChoice !== this.visibleOrderedChoice) {
      this.visibleOrderedChoice = updatedVisibleOrderedChoice;
    }

    const validationResult = this.orderedChoiceService.validateChoice(this.visibleOrderedChoice, this.visibleChoice, this.item.size);
    this.visibleOrderedChoice.warnings = validationResult.valid
      ? []
      : [this.ingredientChoicesType === this.ingredientChoicesTypes.Steps ? validationResult.shortMessage : validationResult.longMessage];
    return action;
  }

  private initVisibleChoice(orderableItem: OrderableItem, resetToFirstChoice?: boolean) {
    if (orderableItem) {
      const hasChoiceGroups = orderableItem.choiceGroups && orderableItem.choiceGroups.length;
      let firstChoiceInGroup = undefined;
      if (hasChoiceGroups) {
        orderableItem.choiceGroups.find(group => {
          firstChoiceInGroup = group.choices.find(choiceName => {
            const choice = this.orderableItem.choices.find(c => c.name === choiceName);
            if (!!choice.dependsOn) {
              return this.orderableItemService.areDependenciesMet(orderableItem, choice);
            } else {
              return true;
            }
          });
          return firstChoiceInGroup;
        });
      }
      const choiceIndex =
        resetToFirstChoice || !this.visibleChoice
          ? hasChoiceGroups
            ? this.findChoiceIndex(firstChoiceInGroup)
            : 0
          : this.findChoiceIndex(this.visibleChoice.name);
      this.choicesService.setVisibleChoice(this.choices[this.replaceWithExpandedListChoiceIndex(choiceIndex)]);
    }
  }

  /**
   * Only for List layouts, prevent expanding the 1st choice by default
   * if another choice has the `isOpenOnWeb` flag
   **/
  private replaceWithExpandedListChoiceIndex(index: number): number {
    if (
      this.ingredientChoicesType === this.ingredientChoicesTypes.List &&
      this.orderableItem.hasExpandedChoices &&
      !this.choices.map((c, i) => (c.isOpenOnWeb ? i : -1)).includes(index)
    ) {
      return this.choices.findIndex(c => c.isOpenOnWeb);
    }
    return index;
  }

  private findChoiceIndex(choice: string): number {
    return this.choices.findIndex(c => choice === c.name);
  }

  private initVisibleChoiceGroup(orderableItem: OrderableItem) {
    if (orderableItem?.choiceGroups?.length) {
      this.choicesService.initSelectedChoiceGroup(
        orderableItem.choiceGroups,
        this.ingredientChoicesType === this.ingredientChoicesTypes.List
      );
    }
  }

  private getOrderedIngredient(choice: IngredientChoice, ingredient: OrderableIngredient): OrderedIngredient | undefined {
    if (this.item) {
      const orderedChoice = this.item.choices.find(c => c.name === choice.name);
      if (orderedChoice) {
        return (orderedChoice.ingredients || []).find(i => i.ingredient === ingredient.name);
      }
    }
    return undefined;
  }

  private addIngredient(choice: IngredientChoice, ingredient: OrderableIngredient, isLeftHalf?: boolean, isRightHalf?: boolean) {
    let orderedChoice = this.item.choices.find(c => c.name === choice.name);
    const dependingChoices = this.orderableItem.choices.filter(ch => ch.dependsOn === ingredient.name);

    if (!orderedChoice) {
      orderedChoice = this.orderedChoiceService.createOrderedChoice(choice);
      this.item.choices.push(orderedChoice);
    }

    if (dependingChoices.length) {
      dependingChoices.forEach(dc => {
        const dependingOrderedChoice = this.orderedChoiceService.createOrderedChoice(dc);
        this.item.choices.push(dependingOrderedChoice);
      });
    }

    if (!orderedChoice.ingredients) {
      orderedChoice.ingredients = [];
    }

    orderedChoice.ingredients.push(OrderedIngredient.createFromOrderable(ingredient, isLeftHalf, isRightHalf));

    if (!choice.dependsOn) {
      this.choicesService.setVisibleOrderedChoice(orderedChoice);
    }

    this.defaultIngredientsPerSizeNotChecked = this.defaultIngredientsPerSizeNotChecked.filter(item => item.name !== ingredient.name);

    return this.addDefaultIngredientsFromDependingChoices(ingredient);
  }

  private addDefaultIngredientsFromDependingChoices(ingredient: OrderableIngredient) {
    return this.orderableItem.choices
      .filter(choice => choice.dependsOn === ingredient.name)
      .forEach(choice => choice.ingredients.filter(i => i.isDefault).forEach(i => this.addIngredient(choice, i)));
  }

  private removeIngredient(choice: IngredientChoice, ingredient: OrderableIngredient, keepVisibleOrderedChoice = false) {
    const orderedChoice = this.item.choices.find(c => c.name === choice.name);
    const dependingChoice = this.orderableItem.choices.filter(ch => ch.dependsOn === ingredient.name);
    if (dependingChoice.length) {
      dependingChoice.forEach(dc => {
        const dependingOrderedChoiceIndex = this.item.choices.findIndex(c => c.name === dc.name);
        if (dependingOrderedChoiceIndex !== -1) {
          this.item.choices.splice(dependingOrderedChoiceIndex, 1);
        }
      });
    }

    if (orderedChoice) {
      const ingredientIndex = (orderedChoice.ingredients || []).findIndex(i => i.ingredient === ingredient.name);
      (orderedChoice.ingredients || []).splice(ingredientIndex, 1);

      if (!keepVisibleOrderedChoice) {
        this.choicesService.setVisibleOrderedChoice(orderedChoice);

        if (ingredient.isDefault) {
          this.defaultIngredientsPerSizeNotChecked.push(ingredient);
        }
      }

      return this.removeIngredientsFromDependingChoices(ingredient);
    }
  }

  private removeIngredientsFromDependingChoices(ingredient: OrderableIngredient) {
    this.orderableItem.choices
      .filter(choice => choice.dependsOn === ingredient.name)
      .forEach(choice => choice.ingredients.forEach(i => this.removeIngredient(choice, i)));
  }

  private validateOrderedItem() {
    this.orderedItemService.validateItem(
      this.item,
      this.orderableItem,
      this.ingredientChoicesType === IngredientChoicesType.Steps,
      this.isHalfAndHalf
    );
    this.isItemValid = this.orderedItemService.isValid(this.item, this.orderableItem);
  }

  private identifyVisibleChoices() {
    if (this.orderableItem && this.item) {
      this.choices = this.rearangeDependants(this.orderedItemService.getFilteredIngredientChoices(this.orderableItem, this.item)).filter(
        choice => !choice.isInstructions
      );
    }
  }

  private rearangeDependants(choices: IngredientChoice[]): IngredientChoice[] {
    return [...choices.filter(c => !c.dependsOn), ...choices.filter(c => c.dependsOn)];
  }

  private selectFirstInvalidStepChoice() {
    if (
      (this.ingredientChoicesType === this.ingredientChoicesTypes.Steps ||
        this.ingredientChoicesType === this.ingredientChoicesTypes.Pills) &&
      this.visibleChoice
    ) {
      const firstInvalidChoice = this.getFirstInvalidChoice();
      if (firstInvalidChoice && this.choiceIndex(this.visibleChoice) > this.choiceIndex(firstInvalidChoice)) {
        this.choicesService.setVisibleChoice(firstInvalidChoice);
      } else {
        this.initVisibleChoice(this.orderableItem, true);
        this.initVisibleChoiceGroup(this.orderableItem);
      }
    }
  }

  private subscribeToVisibleOrderedChoiceChanges(): Subscription {
    return this.choicesService.visibleOrderedChoice$.subscribe(choice => {
      this.visibleOrderedChoice = choice;
      this.identifyVisibleChoices();
      this.calculateNextAndPreviousChoiceGroup();
      this.validateOrderedItem();
      if (choice) {
        const index = this.choices.map(ingredientChoice => ingredientChoice.name).indexOf(choice.name);
        this.nextChoice = index >= 0 ? this.choices[index + 1] : this.nextChoice;
      } else {
        this.nextChoice = undefined;
        this.visibleOrderedChoice = undefined;
      }

      this.isLastChoiceVisible = !this.nextChoice;
      const currentChoiceIndex = this.choiceIndex(this.visibleChoice);
      this.isFirstChoiceVisible = !!this.visibleChoice && currentChoiceIndex === 0;
      this.calcShouldShowBackBtn();
    });
  }

  private subscribeToVisibleChoiceChanges(): Subscription {
    return this.choicesService.visibleChoice$.pipe(distinctUntilChanged()).subscribe((choice: IngredientChoice) => {
      this.visibleChoice = choice;

      if (choice) {
        if (this.item) {
          const visibleOrderedChoice = this.item.getChoice(choice.name);
          this.choicesService.setVisibleOrderedChoice(visibleOrderedChoice);
        }

        this.choiceClicked.emit(this.choicesInit);
        this.choicesInit = false;
        const elementRef = choice && this.scrollLinks ? this.scrollLinks.toArray()[this.choiceIndex(choice)] : undefined;
        if (elementRef) {
          this.scrollLinksWrapper.nativeElement.scrollTo({ top: 0, left: elementRef.nativeElement.offsetLeft, behavior: 'smooth' });
        }
        this.auditService.createAudit(() => `Selected "${choice.name}" choice on "${this.orderableItem.name}" item`);
      } else {
        this.choicesService.setVisibleOrderedChoice(undefined);
      }
    });
  }

  private subscribeToSizeChanges(): Subscription {
    return this.itemEditorService.sizeChanged$.subscribe(size => {
      if (this.isHalfAndHalf) {
        return;
      }
      this.removeUnavailableIngredientsFor(size);
      this.addAvailableChoices(size);
      this.handleDefaultsOnSizeChange(size);
      this.identifyVisibleChoices();
      this.calculateNextAndPreviousChoiceGroup();
      this.validateOrderedItem();
      this.selectFirstInvalidStepChoice();
      this.validateOnSizeChanged();
      this.handledSizeChange.emit();
    });
  }

  private removeUnavailableIngredientsFor(size: string) {
    this.item.choices.forEach(choice => {
      const orderableChoice = this.orderableItem.getChoice(choice.name);
      if (orderableChoice && !!choice.ingredients) {
        for (let i = choice.ingredients.length - 1; i >= 0; i--) {
          const orderableIngredient = orderableChoice.ingredients.find(ing => ing.name === choice.ingredients[i].ingredient);
          if (orderableIngredient && !orderableIngredient.getSizePrice(size)) {
            this.removeIngredient(orderableChoice, orderableIngredient, true);
          }
        }
      }
    });
  }

  private handleDefaultsOnSizeChange(size: string) {
    this.item.choices.forEach(choice => {
      const orderableChoice = this.orderableItem.getChoice(choice.name);
      if (orderableChoice) {
        const tempChoiceIngredients = !!choice.ingredients ? choice.ingredients : [];
        orderableChoice.ingredients
          .filter(
            orderableIngredient =>
              orderableIngredient.isDefault &&
              orderableIngredient.getSizePrice(size) &&
              !tempChoiceIngredients.find(ing => ing.ingredient === orderableIngredient.name) &&
              !this.defaultIngredientsPerSizeNotChecked.find(ingredient => ingredient.name === orderableIngredient.name)
          )
          .map(orderableIngredient => OrderedIngredient.createFromOrderable(orderableIngredient))
          .forEach(orderedIngredient => {
            if (!choice.ingredients) {
              choice.ingredients = [];
            }
            choice.ingredients.push(orderedIngredient);
          });
      }
    });
  }

  private addAvailableChoices(size) {
    const choicesToAdd = this.orderableItem.choices.reduce((to, orderableChoice) => {
      // If choice does not exist on ordered item
      const choiceDoesNotExist = !this.item.choices.find(c => c.name === orderableChoice.name);

      if (!orderableChoice.dependsOn) {
        // If choice doesnt exist and has no dependency add that choice
        return choiceDoesNotExist ? to.concat(this.item.getChoice(orderableChoice.name)) : to;
      }
      // If choice has dependency:
      // Find if the ingredient is available on current size
      const dependsOnExistsOnSize = !!this.orderableItem.choices.find(
        c => !!c.ingredients.find(i => i.name === orderableChoice.dependsOn && !!i.sizePrices.find(s => s.size === this.item.size))
      );
      // Find if the ingredient is allready selected
      const ingredientIsDependant = !!this.item.choices.find(c => !!c.ingredients.find(i => i.ingredient === orderableChoice.dependsOn));
      // Find if the ingredient going to be inserted because its default,
      // its available on current size and not unchecked on previous size changes
      const ingredientWillBeAvailable = !!this.orderableItem.choices.find(choice => {
        return !!choice.ingredients
          .filter(
            orderableIngredient =>
              orderableIngredient.isDefault &&
              orderableIngredient.getSizePrice(size) &&
              !this.defaultIngredientsPerSizeNotChecked.find(ingredient => ingredient.name === orderableIngredient.name)
          )
          .find(i => i.name === orderableChoice.dependsOn);
      });
      return dependsOnExistsOnSize && (ingredientIsDependant || ingredientWillBeAvailable) && choiceDoesNotExist
        ? to.concat(this.item.getChoice(orderableChoice.name))
        : to;
    }, []);
    // Add those choices to ordered item
    this.item.choices.push(...choicesToAdd);
  }

  private determineIfItemHasCalorieInfo(): boolean {
    const hasCalories = (s: SizePrice) => !Utils.isNullOrUndefined(s.caloriesFrom);
    return (
      this.orderableItem.sizePrices.some(hasCalories) ||
      Utils.flatten(this.orderableItem.choices.map(c => c.ingredients)).some((i: OrderableIngredient) => i.sizePrices.some(hasCalories))
    );
  }

  private calculateLeftStepsWrapperPosition() {
    if (
      !this.orderableItem.choiceGroups.length &&
      this.ingredientChoicesType === this.ingredientChoicesTypes.Steps &&
      this.hasIngredientChoices &&
      !this.isMobile
    ) {
      this.leftStepsWrapperPosition = `${this.stepsWrapper?.nativeElement.getBoundingClientRect().left}px`;
    }
  }

  private calculateNextAndPreviousChoiceGroup() {
    if (this.ingredientChoicesType !== this.ingredientChoicesTypes.Steps) {
      return;
    }
    if (this.orderableItem.choiceGroups && this.orderableItem.choiceGroups.length > 1) {
      const filteredGroups = this.itemEditorService.filterChoiceGroupsByDependency(
        this.orderableItem.choiceGroups,
        this.orderableItem.choices,
        this.item
      );
      this.nextChoiceGroup = this.choicesService.getNextChoiceGroup(filteredGroups);
      if (!this.nextChoiceGroup && !this.nextChoice && !this.isLastChoiceVisible) {
        this.onChoiceGroupChange(filteredGroups[0]);
      }
      this.previousChoiceGroup = this.choicesService.getPreviousChoiceGroup(filteredGroups);
    }
  }

  private validateOnSizeChanged() {
    if (!this.item.isValid()) {
      this.editingItemChoices.selectFirstInvalidChoice(this.orderableItem, this.item);
      this.highlightService.highLightAll();
      return;
    }
  }

  /**
   * Scans the ordered item once during init for ingredients that determine recipe
   * and saves the ingredient name so that we can use it in
   * @see toggleIngredients
   * afterwards to decide the recipe changes
   */
  private findOrderedDeterminingRecipeIngredient() {
    this.orderedItemInitialized = true;
    // Get all ordered ingredient names
    const orderedIngredientNames = this.orderedItemService.getAllIngredients(this.item).map(i => i.ingredient);
    // Find the orderable ingredients that are ordered and determine recipe
    const orderableDeterminingIngredients = this.orderableItem.choices
      .map(choice => choice.ingredients)
      .flat(1)
      .filter(ing => ing.determinesRecipe);
    if (!orderableDeterminingIngredients.length || !orderedIngredientNames.length) {
      return;
    }

    // Find if an ingredient that determines recipe has alredy been ordered
    const orderedDeterminingIngredient: OrderableIngredient = orderableDeterminingIngredients.find(
      orderableIng => !!orderedIngredientNames.find(orderedName => orderedName === orderableIng.name)
    );
    // Save its name
    this.changeRecipeTo = orderedDeterminingIngredient?.name ?? null;
  }
}
